import React, { useState, useEffect } from 'react'
import { Check, X, Mail, Calendar, User, AlertCircle, RefreshCw, Filter, Send, RotateCcw } from 'lucide-react'
import { apiClient } from '@/utils/api'
import toast from 'react-hot-toast'
import { format } from 'date-fns'

interface DemoRequest {
  id: string
  email: string
  google_email?: string
  status: 'pending' | 'approved' | 'denied'
  requested_at: string
  approved_at?: string
  approved_by?: string
  notes?: string
  last_login_at?: string
  created_at: string
}

export const DemoRequestsManager: React.FC = () => {
  const [requests, setRequests] = useState<DemoRequest[]>([])
  const [loading, setLoading] = useState(true)
  const [filter, setFilter] = useState<'all' | 'pending' | 'approved' | 'denied'>('pending')
  const [processingIds, setProcessingIds] = useState<Set<string>>(new Set())

  useEffect(() => {
    loadRequests()
  }, [filter])

  const loadRequests = async () => {
    setLoading(true)
    try {
      const params = filter !== 'all' ? `?status=${filter}` : ''
      const data = await apiClient.get(`/api/platform/demo-requests${params}`)
      setRequests(data.requests || [])
    } catch (error) {
      console.error('Failed to load demo requests:', error)
      toast.error('Failed to load demo requests')
    } finally {
      setLoading(false)
    }
  }

  const handleApprove = async (requestId: string, email: string) => {
    if (!confirm(`Approve demo access for ${email}?`)) return

    setProcessingIds(prev => new Set(prev).add(requestId))
    try {
      await apiClient.post(`/api/platform/demo-requests/${requestId}/approve`, {
        notes: 'Approved via admin panel'
      })
      toast.success(`Demo access approved for ${email}`)
      loadRequests()
    } catch (error) {
      console.error('Failed to approve request:', error)
      toast.error('Failed to approve request')
    } finally {
      setProcessingIds(prev => {
        const next = new Set(prev)
        next.delete(requestId)
        return next
      })
    }
  }

  const handleDeny = async (requestId: string, email: string) => {
    const notes = prompt(`Deny demo access for ${email}?\n\nOptional reason:`)
    if (notes === null) return // Cancelled

    setProcessingIds(prev => new Set(prev).add(requestId))
    try {
      await apiClient.post(`/api/platform/demo-requests/${requestId}/deny`, {
        notes: notes || 'Denied via admin panel'
      })
      toast.success(`Demo access denied for ${email}`)
      loadRequests()
    } catch (error) {
      console.error('Failed to deny request:', error)
      toast.error('Failed to deny request')
    } finally {
      setProcessingIds(prev => {
        const next = new Set(prev)
        next.delete(requestId)
        return next
      })
    }
  }

  const handleResend = async (requestId: string, email: string) => {
    if (!confirm(`Resend invitation email to ${email}?`)) return

    setProcessingIds(prev => new Set(prev).add(requestId))
    try {
      await apiClient.post(`/api/platform/demo-requests/${requestId}/resend`, {})
      toast.success(`Invitation resent to ${email}`)
      loadRequests()
    } catch (error) {
      console.error('Failed to resend invitation:', error)
      toast.error('Failed to resend invitation')
    } finally {
      setProcessingIds(prev => {
        const next = new Set(prev)
        next.delete(requestId)
        return next
      })
    }
  }

  const handleChangeStatus = async (requestId: string, email: string, newStatus: 'approved' | 'denied') => {
    const action = newStatus === 'approved' ? 'approve' : 'deny'
    const notes = prompt(`Change status to ${newStatus} for ${email}?\n\nOptional reason:`)
    if (notes === null) return // Cancelled

    setProcessingIds(prev => new Set(prev).add(requestId))
    try {
      await apiClient.patch(`/api/platform/demo-requests/${requestId}/status`, {
        status: newStatus,
        notes: notes || `Changed to ${newStatus} via admin panel`
      })
      toast.success(`Status changed to ${newStatus} for ${email}`)
      loadRequests()
    } catch (error) {
      console.error(`Failed to ${action} request:`, error)
      toast.error(`Failed to ${action} request`)
    } finally {
      setProcessingIds(prev => {
        const next = new Set(prev)
        next.delete(requestId)
        return next
      })
    }
  }

  const getStatusBadge = (status: string) => {
    const styles = {
      pending: 'bg-yellow-100 text-yellow-800 border-yellow-200',
      approved: 'bg-green-100 text-green-800 border-green-200',
      denied: 'bg-red-100 text-red-800 border-red-200',
    }
    return (
      <span className={`px-3 py-1 rounded-full text-xs font-medium border ${styles[status as keyof typeof styles]}`}>
        {status.charAt(0).toUpperCase() + status.slice(1)}
      </span>
    )
  }

  const pendingCount = requests.filter(r => r.status === 'pending').length

  return (
    <div>
      <div className="mb-6">
        <div className="flex items-center justify-between mb-4">
          <div>
            <h2 className="text-2xl font-bold text-gray-900">Demo Access Requests</h2>
            <p className="text-gray-600">Manage access requests for the demo family</p>
          </div>
          <button
            onClick={loadRequests}
            disabled={loading}
            className="flex items-center space-x-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50"
          >
            <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
            <span>Refresh</span>
          </button>
        </div>

        {/* Filters */}
        <div className="flex items-center space-x-2">
          <Filter className="w-4 h-4 text-gray-500" />
          <div className="flex space-x-2">
            {(['all', 'pending', 'approved', 'denied'] as const).map(status => (
              <button
                key={status}
                onClick={() => setFilter(status)}
                className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
                  filter === status
                    ? 'bg-blue-600 text-white'
                    : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                }`}
              >
                {status.charAt(0).toUpperCase() + status.slice(1)}
                {status === 'pending' && pendingCount > 0 && (
                  <span className="ml-2 bg-white text-blue-600 px-2 py-0.5 rounded-full text-xs">
                    {pendingCount}
                  </span>
                )}
              </button>
            ))}
          </div>
        </div>
      </div>

      {/* Pending Alert */}
      {pendingCount > 0 && filter !== 'pending' && (
        <div className="mb-4 bg-yellow-50 border border-yellow-200 rounded-lg p-4 flex items-center justify-between">
          <div className="flex items-center space-x-3">
            <AlertCircle className="w-5 h-5 text-yellow-600" />
            <span className="text-yellow-800">
              {pendingCount} pending request{pendingCount !== 1 ? 's' : ''} awaiting review
            </span>
          </div>
          <button
            onClick={() => setFilter('pending')}
            className="text-yellow-700 hover:text-yellow-900 font-medium"
          >
            View Pending
          </button>
        </div>
      )}

      {/* Requests List */}
      {loading ? (
        <div className="flex items-center justify-center h-64">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-500"></div>
        </div>
      ) : requests.length === 0 ? (
        <div className="bg-white rounded-lg shadow p-12 text-center">
          <Mail className="w-12 h-12 text-gray-400 mx-auto mb-4" />
          <h3 className="text-lg font-semibold text-gray-900 mb-2">No requests found</h3>
          <p className="text-gray-600">
            {filter === 'pending'
              ? 'No pending demo access requests at this time.'
              : `No ${filter} demo access requests.`
            }
          </p>
        </div>
      ) : (
        <div className="bg-white rounded-lg shadow overflow-hidden">
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    User
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Status
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Requested
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Last Login
                  </th>
                  <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {requests.map(request => {
                  const isProcessing = processingIds.has(request.id)
                  return (
                    <tr key={request.id} className={isProcessing ? 'opacity-50' : ''}>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="flex items-center">
                          <div className="flex-shrink-0 h-10 w-10 bg-blue-100 rounded-full flex items-center justify-center">
                            <User className="w-5 h-5 text-blue-600" />
                          </div>
                          <div className="ml-4">
                            <div className="text-sm font-medium text-gray-900">{request.email}</div>
                            {request.google_email && request.google_email !== request.email && (
                              <div className="text-sm text-gray-500">{request.google_email}</div>
                            )}
                          </div>
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        {getStatusBadge(request.status)}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="flex items-center text-sm text-gray-900">
                          <Calendar className="w-4 h-4 mr-2 text-gray-400" />
                          {format(new Date(request.requested_at), 'MMM d, yyyy')}
                        </div>
                        <div className="text-xs text-gray-500">
                          {format(new Date(request.requested_at), 'h:mm a')}
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                        {request.last_login_at ? (
                          <div>
                            <div>{format(new Date(request.last_login_at), 'MMM d, yyyy')}</div>
                            <div className="text-xs">{format(new Date(request.last_login_at), 'h:mm a')}</div>
                          </div>
                        ) : (
                          <span className="text-gray-400">Never</span>
                        )}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                        <div className="flex items-center justify-end space-x-2">
                          {request.status === 'pending' && (
                            <>
                              <button
                                onClick={() => handleApprove(request.id, request.email)}
                                disabled={isProcessing}
                                className="inline-flex items-center px-3 py-1.5 bg-green-600 text-white rounded-lg hover:bg-green-700 disabled:opacity-50 transition-colors"
                                title="Approve request"
                              >
                                <Check className="w-4 h-4 mr-1" />
                                Approve
                              </button>
                              <button
                                onClick={() => handleDeny(request.id, request.email)}
                                disabled={isProcessing}
                                className="inline-flex items-center px-3 py-1.5 bg-red-600 text-white rounded-lg hover:bg-red-700 disabled:opacity-50 transition-colors"
                                title="Deny request"
                              >
                                <X className="w-4 h-4 mr-1" />
                                Deny
                              </button>
                            </>
                          )}
                          {request.status === 'approved' && (
                            <>
                              <button
                                onClick={() => handleResend(request.id, request.email)}
                                disabled={isProcessing}
                                className="inline-flex items-center px-3 py-1.5 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 transition-colors"
                                title="Resend invitation email"
                              >
                                <Send className="w-4 h-4 mr-1" />
                                Resend
                              </button>
                              <button
                                onClick={() => handleChangeStatus(request.id, request.email, 'denied')}
                                disabled={isProcessing}
                                className="inline-flex items-center px-3 py-1.5 bg-gray-600 text-white rounded-lg hover:bg-gray-700 disabled:opacity-50 transition-colors"
                                title="Change to denied"
                              >
                                <X className="w-4 h-4 mr-1" />
                                Deny
                              </button>
                            </>
                          )}
                          {request.status === 'denied' && (
                            <button
                              onClick={() => handleChangeStatus(request.id, request.email, 'approved')}
                              disabled={isProcessing}
                              className="inline-flex items-center px-3 py-1.5 bg-green-600 text-white rounded-lg hover:bg-green-700 disabled:opacity-50 transition-colors"
                              title="Change to approved"
                            >
                              <RotateCcw className="w-4 h-4 mr-1" />
                              Approve
                            </button>
                          )}
                        </div>
                      </td>
                    </tr>
                  )
                })}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Notes */}
      {requests.some(r => r.notes) && (
        <div className="mt-6 bg-gray-50 rounded-lg p-4">
          <h4 className="font-semibold text-gray-900 mb-3">Recent Notes</h4>
          <div className="space-y-2">
            {requests
              .filter(r => r.notes)
              .slice(0, 5)
              .map(r => (
                <div key={r.id} className="text-sm">
                  <span className="font-medium">{r.email}:</span>
                  <span className="text-gray-600 ml-2">{r.notes}</span>
                </div>
              ))}
          </div>
        </div>
      )}
    </div>
  )
}
